import { fmtDuration } from "@/lib/time";
import { cleanTask, subTotal, taskTotal } from "@/lib/tasks";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const small = "rounded-lg bg-white border-[#103080]/20 h-9 text-sm";

function PersonSelect({ value, onChange, directory, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`${small} w-36`} data-testid={testId}><SelectValue placeholder="Assign" /></SelectTrigger>
      <SelectContent>{directory.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function planFromTemplate(tpl, assignee, roles = []) {
  const forRole = (r) => roles.find((x) => x.name.toLowerCase() === (r || "").toLowerCase())?.member_ids[0];
  return tpl.tasks.map((t) => ({
    title: t.title, description: t.description, duration_minutes: t.duration_minutes, sets_status: t.sets_status || "",
    assignee_id: forRole(t.role) || assignee, offset_days: t.offset_days, subtasks: t.subtasks, role: t.role,
  }));
}

export const planTotal = (plan) => plan.reduce((s, t) => s + taskTotal(t), 0);

export default function TaskPlanEditor({ service, templates, roles = [], directory, defaultAssignee, value, onChange, testPrefix }) {
  const options = templates.filter((t) => !service || t.service === service || t.service === "other");
  const plan = value.task_plan || [];
  const planRoles = [...new Set(plan.map((t) => t.role).filter(Boolean))];
  const pick = (id) => {
    const tpl = templates.find((t) => t.id === id);
    onChange({ template_id: tpl ? id : null, task_plan: tpl ? planFromTemplate(tpl, defaultAssignee, roles) : [] });
  };
  const setTask = (i, patch) => onChange({ ...value, task_plan: plan.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const setRole = (role, id) => onChange({ ...value, task_plan: plan.map((t) => (t.role === role ? { ...t, assignee_id: id } : t)) });

  return (
    <div className="sm:col-span-2 space-y-3" data-testid={`${testPrefix}-plan`}>
      <div className="space-y-1.5">
        <Label>Template</Label>
        <Select value={value.template_id || "none"} onValueChange={pick}>
          <SelectTrigger className="rounded-xl bg-white border-[#103080]/20 h-10" data-testid={`${testPrefix}-template-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No template (one task)</SelectItem>
            {options.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`${testPrefix}-template-option-${t.id}`}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {planRoles.length > 0 && (
        <div className="flex flex-wrap gap-3 rounded-xl bg-[#F5F2EB] p-3">
          {planRoles.map((r) => (
            <div key={r} className="flex items-center gap-2 text-xs font-semibold text-[#103080]">
              {r}: <PersonSelect value="" onChange={(id) => setRole(r, id)} directory={directory} testId={`${testPrefix}-role-${r.replace(/\s+/g, "-").toLowerCase()}`} />
            </div>
          ))}
        </div>
      )}
      {plan.length > 0 && (
        <ul className="rounded-xl border border-[#103080]/10 bg-white divide-y divide-[#103080]/10 max-h-72 overflow-y-auto thin-scroll">
          {plan.map((t, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2 p-2.5" data-testid={`${testPrefix}-task-${i}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#0090D0] font-semibold">{t.role || "No role"}<span className="text-[#0F172A]/40"> · {t.subtasks?.length || 0} subtasks</span></p>
                <p className="text-sm text-[#103080] leading-snug">{t.title}</p>
              </div>
              <PersonSelect value={t.assignee_id} onChange={(id) => setTask(i, { assignee_id: id })} directory={directory} testId={`${testPrefix}-task-${i}-assignee`} />
              {subTotal(t.subtasks) > 0 ? <span className="text-xs font-semibold text-[#0090D0] w-20 text-right" data-testid={`${testPrefix}-task-${i}-total`}>{fmtDuration(taskTotal(t))}</span> : (<>
              <Input type="number" min={5} step={5} value={t.duration_minutes} onChange={(e) => setTask(i, { duration_minutes: e.target.value })}
                className={`${small} w-20`} data-testid={`${testPrefix}-task-${i}-minutes`} aria-label="Minutes" />
              <span className="text-xs text-[#0F172A]/50">min</span></>)}
            </li>
          ))}
        </ul>
      )}
      {plan.length > 0 && <p className="text-xs text-[#103080] font-semibold" data-testid={`${testPrefix}-plan-total`}>{plan.length} tasks · {fmtDuration(planTotal(plan))} in total</p>}
    </div>
  );
}

export const cleanPlan = (plan) => plan.map(cleanTask);
